import { useEffect, useId, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useRegisterOverlay } from '@/stores/overlayStore';
import { useBackCloses } from './bottomSheetHistory';

export interface BottomSheetProps {
  open: boolean;
  /** Centered in the header, and the dialog's accessible name. */
  title: string;
  /** ✕, scrim tap, swipe down, Escape and Back all call this. */
  onClose: () => void;
  /** aria-label of the ✕. */
  closeLabel?: string;
  children: ReactNode;
  testId?: string;
}

/** A downward drag at least this long closes the sheet. */
const SWIPE_CLOSE_PX = 80;
/**
 * Movement before a press counts as a drag and the sheet captures the
 * pointer. Below it, it is a tap: no capture, so the button under the finger
 * still receives its click.
 */
const DRAG_SLOP_PX = 8;

/**
 * The design system's `.bottom sheet`, Height=content (Figma 11611:8319): a
 * card pinned to the bottom of the screen that hugs its body, inside the
 * end-to-end `overlay modal` scrim.
 *
 * The way out sits top LEFT (`.sticky modal header` Close=left, Kelvin
 * Oct 27): a sheet slides over the page, so its ✕ goes where a page's back
 * button is. A scrim tap, a swipe down, Escape and Back close it too. No grabber:
 * Yaruta's sheets never resize between heights.
 */
export function BottomSheet({
  open,
  title,
  onClose,
  closeLabel = 'Close',
  children,
  testId,
}: BottomSheetProps) {
  const titleId = useId();
  const dragStartY = useRef<number | null>(null);
  const capturedPointer = useRef<number | null>(null);
  const [dragY, setDragY] = useState(0);

  // Pauses (and hides) the bottom toast while this covers it (YAR-476).
  useRegisterOverlay(open);
  useBackCloses(open, onClose);

  // Close on Escape and lock body scroll while open, as FullScreenSheet does.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handler);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    dragStartY.current = e.clientY;
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (dragStartY.current === null) return;
    const distance = e.clientY - dragStartY.current;
    // Once it is a drag, keep every move and the release on the sheet even
    // when the finger leaves it (jsdom has no pointer capture).
    const el = e.currentTarget;
    if (
      capturedPointer.current === null &&
      distance > DRAG_SLOP_PX &&
      typeof el.setPointerCapture === 'function'
    ) {
      el.setPointerCapture(e.pointerId);
      capturedPointer.current = e.pointerId;
    }
    // Follows the finger down only; an upward drag leaves it where it is.
    setDragY(Math.max(0, distance));
  };
  const releaseCapture = (el: HTMLDivElement) => {
    const id = capturedPointer.current;
    capturedPointer.current = null;
    if (id !== null && typeof el.releasePointerCapture === 'function') {
      el.releasePointerCapture(id);
    }
  };
  const endDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    releaseCapture(e.currentTarget);
    if (dragStartY.current === null) return;
    const distance = e.clientY - dragStartY.current;
    dragStartY.current = null;
    setDragY(0);
    if (distance >= SWIPE_CLOSE_PX) onClose();
  };
  const cancelDrag = (e: ReactPointerEvent<HTMLDivElement>) => {
    releaseCapture(e.currentTarget);
    dragStartY.current = null;
    setDragY(0);
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[200] flex flex-col justify-end"
      data-testid={testId}
    >
      {/* Scrim: Figma `overlay modal` Type=end-to-end, the same 30% wash
          and 5px blur as Modal's backdrop. */}
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-[5px]"
        onClick={onClose}
        data-testid={testId ? `${testId}-scrim` : undefined}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={cancelDrag}
        style={dragY > 0 ? { transform: `translateY(${dragY}px)` } : undefined}
        // touch-none: without it a phone claims the vertical drag for
        // scrolling / pull-to-refresh and cancels the pointer (review I-2).
        // The sheet hugs its content, so there is nothing of its own to scroll.
        className="relative flex w-full touch-none flex-col gap-3 rounded-t-lg bg-card px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))] text-card-foreground"
      >
        {/* `.sticky modal header`, Close=left: ✕ first, title centred
            between two equal flex columns so it stays centred. */}
        <div className="flex items-center py-2">
          <div className="flex min-w-0 flex-1 items-center">
            <button
              type="button"
              onClick={onClose}
              aria-label={closeLabel}
              className="inline-flex h-11 cursor-pointer items-center justify-center rounded-sm px-2.5 py-2 text-icon-default transition-colors duration-150 hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              <X
                size={24}
                strokeWidth={1.5}
              />
            </button>
          </div>
          <h2
            id={titleId}
            className="m-0 shrink-0 whitespace-nowrap text-center text-base font-bold leading-6 text-text-heading"
          >
            {title}
          </h2>
          <div className="h-11 min-w-0 flex-1" />
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}
